'use strict';

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

import {Card} from 'primereact/card';

import {
    __,
    Placeholders
} from '~/shared/src/utilities/messageCatalog';
import {LoadDataParameters} from '~/shared/src/utilities/modules/calendar';
import {
    Alignment,
    Visibility
} from '~/shared/src/utilities/modules/form';

import Graph from '~/frontend/src/components/base/Graph';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';

import {getHeightStyle} from '~/frontend/src/components/base/shared/field/valueTypeFields/TextField';
import {MessageResolver} from '~/frontend/src/components/base/shared/field/FieldWrapper';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';


type DefaultProps = {

    /**
     * The CSS class name(s) to add to the wrapper.
     */
    classNames? : string[];

    /**
     * Is he calendar disabled?
     */
    disabled? : boolean;

    /**
     * How the view to display as?
     */
    displayMode? : 'card' | 'field';

    /**
     * The number of text rows to be displayed within the calendar.
     */
    height? : number;

    /**
     * Should the calendar marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The max number of text rows to be displayed within the calendar.
     * 0 means unlimited
     */
    maxHeight? : number;

    /**
     * The styles to style the wrapper with.
     */
    style? : React.CSSProperties;

    /**
     * The alignment of the title for the calendar.
     */
    titleAlignment? : Alignment;

    /**
     * The CSS class names to title the label with.
     */
    titleClassNames? : string[];

    /**
     * The styles to style the title with.
     */
    titleStyle? : React.CSSProperties;

    /**
     * The visibility of the title for the calendar.
     */
    titleVisibility? : Visibility;

    /**
     * The visibility of the field
     */
    visibility? : Visibility;

};
const defaultProps : Required<DefaultProps> = {
    classNames     : [ 'yn-field-wrapper', 'field' ],
    disabled       : false,
    displayMode    : 'field',
    height         : 0,
    markDirty      : false,
    maxHeight      : 0,
    style          : {},
    titleAlignment : 'left',
    titleClassNames: [ 'yn-field-label' ],
    titleStyle     : {},
    titleVisibility: 'visible',
    visibility     : 'visible'
};

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    name : string;

    id? : string;

    viewKey : string;

    dataContext : string;

    formUid : string;

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and form contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     * Cypher or SQL based query:
     *   - key: ${PlaceholderAsWrittenInQuery}; value: ${<DataObjectOfForm.itemKey>.<attributePath>}
     */
    additionalParameters? : Required<LoadDataParameters>[ 'parameters' ];

    /**
     * The placeholders for messages translated by MessageCatalog.
     */
    messagePlaceholders? : Placeholders;

    /**
     * A resolver function for message templateStrings.
     *
     * @param templateString
     */
    messageTemplateStringResolver? : MessageResolver;

    /**
     * The title for the calendar.
     */
    title? : string;

    /**
     * PassTrough options
     */
    pt? : {
        root? : React.HTMLAttributes<HTMLDivElement> & { [ key : `data-${string}` ] : string };
    };
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;

type ElusiveData = {
    graphHeight? : string;
};


const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;


const GraphView = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const dispatch : AppDispatch                = useDispatch(),
              {
                  additionalParameters,
                  classNames,
                  dataContext,
                  //disabled,
                  displayMode,
                  formUid,
                  height,
                  id,
                  //markDirty,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  //name,
                  pt,
                  style,
                  title,
                  titleAlignment,
                  titleClassNames,
                  titleStyle,
                  titleVisibility,
                  uid,
                  viewKey
              }                                     = props,
              elementId                             = id ?? `${uid}_graphView`,
              graphUid                              = `${uid}_graphView`,
              user                                  = useSelector( ( state : StoreState ) => state.session?.user ),
              graphProps                            = useSelector( ( state : StoreState ) => {
                  let retVal : PanelProps | undefined = getSubPanelProps( graphUid, state );

                  if ( Object.keys( retVal ).length === 0 ) {
                      retVal = undefined;
                  }

                  return retVal;
              } ),
              elusiveData                           = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
              observedGraphPanelElement             = useRef<Element | undefined>( undefined ),
              cRenderToggle                         = useRef<number>( 0 ),
              timeoutId                             = useRef<NodeJS.Timeout | undefined>(),
              [ rerenderToggle, setRerenderToggle ] = useState<number>( 0 ),
              messageResolver                       = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      messagePlaceholders,
                      messageTemplateStringResolver
                  ]
              ),
              resolvedTitle                         = useMemo(
                  () => {
                      return title ? messageResolver( title ) : undefined;
                  },
                  [
                      JSON.stringify( title ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              graphElement                          = document.getElementById( `${graphUid}_graph` ),
              graphPanelElement                     = useMemo(
                  () => graphElement?.closest( '.yn-content-panel-container' ),
                  [
                      graphElement
                  ]
              ),
              graphFormElement                      = useMemo(
                  () => graphElement?.closest( '.yn-form' ),
                  [
                      graphElement
                  ]
              ),
              graphContainerElement                 = useMemo(
                  () => graphElement?.closest( '.yn-form-card:not( .yn-view-card )' )
                      ?? graphElement?.closest( '.yn-form-tabpanel' )
                      ?? graphElement?.closest( '.yn-form-container' )
                      ?? graphElement?.closest( '.yn-form-layout-wrapper' ),
                  [
                      graphElement
                  ]
              ),
              graphFormContentElement               = useMemo(
                  () => graphFormElement?.querySelector( '.yn-form-container-content' ),
                  [
                      graphFormElement
                  ]
              ),
              graphContainerContainerElement        = useMemo(
                  () => graphContainerElement?.parentElement?.closest( '.yn-form-card:not( .yn-view-card )' )
                      ?? graphContainerElement?.parentElement?.closest( '.yn-form-tabpanel' )
                      ?? graphContainerElement?.parentElement?.closest( '.yn-form-container' )
                      ?? graphContainerElement?.parentElement?.closest( '.yn-form-layout-wrapper' ),
                  [
                      graphContainerElement
                  ]
              ),
              graphContainerContentElement          = useMemo(
                  () => graphContainerElement?.querySelector( '.yn-form-container-content' ),
                  [
                      graphContainerElement
                  ]
              ),
              graphHeightToSet                      = useMemo(
                  () => elusiveData?.graphHeight,
                  [
                      elusiveData
                  ]
              ),
              heightStyle                           = useMemo(
                  () => {
                      const retVal = getHeightStyle( {}, height, maxHeight, 'hidden', 'left', 'hidden' );

                      retVal.minHeight = '20rem';

                      if ( graphHeightToSet ) {
                          retVal.height = graphHeightToSet;
                      }

                      return retVal;
                  },
                  [
                      graphHeightToSet,
                      height,
                      maxHeight
                  ]
              ),
              graph                                 = useMemo(
                  () => {
                      return <Graph
                          uid={graphUid}
                          dataContext={dataContext}
                          formUid={formUid}
                          viewKey={viewKey}
                          additionalQueryParameters={additionalParameters}
                          {...graphProps}
                      />;
                  },
                  [
                      additionalParameters,
                      dataContext,
                      formUid,
                      graphProps,
                      graphUid,
                      viewKey
                  ]
              ),
              resizeHandler                         = useCallback(
                  () => {
                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      timeoutId.current = setTimeout(
                          setRerenderToggle.bind( null, cRenderToggle.current++ ),
                          25
                      );
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              resizeObserver                        = useMemo(
                  () => new ResizeObserver( resizeHandler ),
                  [
                      resizeHandler
                  ]
              );

        cRenderToggle.current = rerenderToggle;

        // ( on mount / unmount )
        // remove observed element from resizeObserver
        useEffect(
            () => {
                // unmount
                return () => {
                    if ( observedGraphPanelElement.current ) {
                        resizeObserver.unobserve( observedGraphPanelElement.current );
                    }
                }
            },
            []
        );

        // update observed element of resizeObserver
        useEffect(
            () => {
                if ( observedGraphPanelElement.current ) {
                    resizeObserver.unobserve( observedGraphPanelElement.current );
                }
                if ( graphPanelElement ) {
                    observedGraphPanelElement.current = graphPanelElement;
                    resizeObserver.observe( graphPanelElement );
                }
            },
            [
                graphPanelElement
            ]
        );

        // update graphHeight
        useEffect(
            () => {
                if (
                    height === 0
                    && graphFormElement
                    && graphPanelElement?.clientHeight
                    && graphFormContentElement?.children?.length === 1
                    && graphContainerElement?.classList.contains( 'yn-form-layout-wrapper' )
                    && !graphContainerContainerElement
                    && graphContainerContentElement?.children?.length === 1
                ) {
                    let graphHeight = `calc( ${graphPanelElement.clientHeight}px - 4rem )`;

                    if ( ( graphFormElement as HTMLElement ).classList.contains( 'yn-form-designer' ) ) {
                        graphHeight = `calc( ${graphPanelElement.clientHeight - 2}px - 5.5rem )`
                    }

                    dispatch( setElusiveData( uid, { graphHeight } ) );
                }
                else {
                    dispatch( setElusiveData( uid, { graphHeight: undefined } ) );
                }
            },
            [
                height,
                graphFormElement,
                graphPanelElement?.clientHeight,
                graphFormContentElement?.children?.length,
                graphContainerElement,
                graphContainerContainerElement,
                graphContainerContentElement?.children?.length
            ]
        );

        return useMemo(
            () => {
                if ( displayMode === 'card' ) {
                    return <div
                        {...pt?.root}
                        id={elementId}
                        key={elementId}
                        className={`yn-form-card yn-view-card${graphHeightToSet ? ' yn-view-card-standalone' : ''}`}
                        style={style}
                    >
                        <Card
                            id={`${elementId}_card`}
                            key={`${elementId}_card`}
                            title={resolvedTitle ? <span style={titleStyle}>{resolvedTitle}</span> : undefined}
                            className={`yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility}`}
                        >
                            <div
                                className={`yn-view-wrapper yn-view-wrapper-graph yn-view-wrapper-card ${classNames.join( ' ' )}`}
                                style={{ ...heightStyle, ...style }}
                            >
                                <div
                                    id={`${elementId}_border`}
                                    key={`${elementId}_border`}
                                    className="yn-view-wrapper-border"
                                >
                                    <LoadingMaskPanel
                                        key={`${graphUid}_loadingMask`}
                                        forComponent={graphUid}
                                        className="yn-view-loadingmask yn-graphview-loadingmask"
                                        style={{ width: '100%' }}
                                    >
                                        {graph}
                                    </LoadingMaskPanel>
                                </div>
                            </div>
                        </Card>
                    </div>;
                }
                else {
                    return <div
                        {...pt?.root}
                        id={elementId}
                        key={elementId}
                        className="yn-form-view"
                        style={style}
                    >
                        <LoadingMaskPanel
                            key={`${graphUid}_loadingMask`}
                            forComponent={graphUid}
                            className="yn-view-loadingmask yn-graphview-loadingmask"
                            style={{ width: '100%' }}
                        >
                            <div
                                {...pt?.root}
                                id={elementId}
                                key={elementId}
                                className={`yn-view-wrapper ${classNames.join( ' ' )}`}
                                style={{ ...heightStyle }}
                            >
                                {
                                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                                    ( title || titleVisibility === 'placeholder' ) &&
                                    <div className={`yn-form-container-title yn-title-align-${titleAlignment} yn-title-visibility-${titleVisibility} ${titleClassNames.join( ' ' )}`} style={titleStyle}>{resolvedTitle ?? ' '}</div>
                                }
                                {graph}
                            </div>
                        </LoadingMaskPanel>
                    </div>;
                }
            },
            [
                graph,
                graphHeightToSet,
                graphUid,
                classNames,
                elementId,
                heightStyle,
                pt,
                resolvedTitle,
                style,
                title,
                titleAlignment,
                titleClassNames,
                titleStyle,
                titleVisibility
            ]
        );
    }
};
GraphView.defaultProps = defaultProps;
GraphView.displayName = 'GraphView';

export default GraphView;
